import { Fragment } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageList from '../../templates/PageList';
import SelectFilter from '../../organisms/SelectFilter';
import PageSearch from '../../molecules/PageSearch';
import { useSignal } from '@preact/signals-react';
import DocumentListModal from './DocumentListModal';
import { ErrorItemType } from '../../atoms/SpanList';
import appConfig from '../../../config/app-config';
import ReportStatusBadge from './ReportStatusBadge';
import NewTooltip from '../../atoms/NewTooltip';
import { getContentByStatus } from './batchReportUtils';

const DATA_PROVIDER_ENV = appConfig.apis.dataProviderApiEnv;
const BASE_URL = `${DATA_PROVIDER_ENV.address}/batch-reports/page-list`;
const API_KEY = DATA_PROVIDER_ENV.key;

interface BatchReport {
    id: string;
    identification: string;
    sk: string;
    inc: string;
    provider: string;
    reportType: string;
    createdAt: string;
    status: string;
    documentsCount: number;
    errorCount: number;
    successCount: number;
    sendingCount: number;
    statusFormatted: string;
}

interface ReportType {
    id: string;
    name: string;
    description: string;
    provider: string;
    active?: boolean;
    documentType?: string;
}


const BatchReportPageList = () => {
    const navigate = useNavigate();
    const { page: pageParam, pageSize: pageSizeParam, field: fieldSorted, order: orderSorted, filter }: any = useParams();

    const page = Number(pageParam) || 0;
    const pageSize = Number(pageSizeParam) || 20;

    const showModal = useSignal(false);
    const modalTitle = useSignal('');
    const modalItems = useSignal<string[]>([]);
    const modalType = useSignal<ErrorItemType>(ErrorItemType.SUCCESS);
    const highlightedRowId = useSignal<string | null>(null);

    const providers = useSignal<string[]>([]);
    const reportTypes = useSignal<ReportType[]>([]);

    let provider = '';
    let rptType = '';
    let status = '';
    let identification = '';
    if (filter) {
        [provider = '', rptType = '', status = '', identification = ''] = filter.match(/=(.*?);/g).map((m: string) => m.slice(1, -1));
    }

    const params = new URLSearchParams({
        provider,
        reportType: rptType,
        status,
        identification,
        excludePrefix: 'CAE-',
        page: String(page),
        pageSize: String(pageSize),
        order: `${fieldSorted},${orderSorted}`,
    });
    const url = `${BASE_URL}?${params.toString()}`;

    const renderRows = (rows: BatchReport[]) => {
        if (!rows || rows.length === 0) {
            return (
                <tr>
                    <td colSpan={8} className="py-2 px-2 text-center">
                        <div className="text-2xl font-bold text-gray-500 mt-4">Nenhum registro encontrado...</div>
                    </td>
                </tr>
            );
        }

        return rows.map((row: BatchReport, index: number) => (
            <tr
                key={row.id}
                onClick={() => navigate(`/data-provider/batch-report/view/${row.sk}/0/100/document/ASC`)}
                className={`${index % 2 === 0 ? 'bg-slate-100 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-300 hover:shadow-md cursor-pointer border-b border-gray-300`}
            >
                <td className="py-2 px-2 text-center">{row.inc}</td>
                <td className="py-2 px-2 text-left" title={row.identification}>
                    {row.identification.length > 30 ? `${row.identification.substring(0, 30)}...` : row.identification}
                </td>
                <td className="py-2 px-2 text-center">{row.provider}</td>
                <td className="py-2 px-2 text-center">{row.reportType}</td>
                <td className="py-2 px-2 text-center">{row.createdAt}</td>
                <td className="py-2 px-2 text-center">
                    <span>{row.documentsCount}</span>
                </td>
                <td className="py-2 px-2 text-center">
                    <span>{row.errorCount}</span>
                </td>
                <td className="py-2 px-2 text-center">
                    <span>{row.successCount}</span>
                </td>
                <td className="py-2 px-2 text-center">
                    <span>{(row.sendingCount ?? 0)}</span>
                </td>
                <td className="py-2 px-2 text-center">
                    <NewTooltip content={getContentByStatus(row.status)} alignment="left">
                        <ReportStatusBadge status={row.status} />
                    </NewTooltip>
                </td>
            </tr>
        ));
    };

    const pageChanged = (p: number) => {
        navigate(urlNavigate(p, pageSize, fieldSorted, orderSorted, provider, rptType, status, identification), { replace: true });
    };

    const pageSizeChanged = (size: number) => {
        navigate(urlNavigate(0, size, fieldSorted, orderSorted, provider, rptType, status, identification), { replace: true });
    };

    const pageSorted = (field: keyof BatchReport | string, order: 'ASC' | 'DESC') => {
        navigate(urlNavigate(page, pageSize, field, order, provider, rptType, status, identification), { replace: true });
    };

    const providerFilterChanged = (value: string) => {
        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, value, rptType, status, identification), { replace: true });
    };

    const reportTypeFilterChanged = (value: string) => {
        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, provider, value, status, identification), { replace: true });
    };

    const statusFilterChanged = (value: string) => {
        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, provider, rptType, value, identification), { replace: true });
    };

    const identificationFilterChanged = (value: string) => {
        navigate(urlNavigate(0, pageSize, fieldSorted, orderSorted, provider, rptType, status, value), { replace: true });
    };

    const urlNavigate = (p: number, size: number, field: string, order: string, prov: string, type: string, stat: string, ident: string) => {
        return `/data-provider/batch-report/${p}/${size}/${field}/${order}/p=${prov};t=${type};s=${stat};i=${ident};`;
    };

    return (
        <Fragment>
            <PageList
                key={`${provider}-${rptType}-${status}-${identification}`}
                pageTitle="Relatórios em lote"
                url={url}
                fetchHeaders={[{ key: 'x-api-key', value: API_KEY }]}
                breadcrumb={[{ label: 'Relatórios em lote', url: '#' }]}
                tableFieldSort={fieldSorted}
                tableOrderSort={orderSorted}
                onTableSort={pageSorted}
                renderRows={renderRows}
                page={page}
                pageSize={pageSize}
                onPageChange={pageChanged}
                onItemsPerPageChange={pageSizeChanged}
                itemsLabel="lotes"
                defaultBackendHeader={false}
                tableHeaders={[
                    { id: 'id', title: '#', field: 'inc', width: '80px', canSort: false },
                    { id: 'identification', title: 'Identificação', field: 'identification', canSort: false },
                    { id: 'provider', title: 'Provedor', field: 'provider', width: '170px', canSort: false },
                    { id: 'reportType', title: 'Tipo de relatório', field: 'reportType', width: '240px', canSort: false },
                    { id: 'createdAt', title: 'Criado em', field: 'createdAt', width: '130px', canSort: false },
                    { id: 'documents', title: 'Documentos', field: 'documents', width: '110px', canSort: false },
                    { id: 'errors', title: 'Erros', field: 'errors', width: '80px', canSort: false },
                    { id: 'success', title: 'Sucesso', field: 'success', width: '80px', canSort: false },
                    { id: 'sending', title: 'Enviando', field: 'sending', width: '90px', canSort: false },
                    { id: 'status', title: 'Status', field: 'status', width: '110px', canSort: false },
                ]}
                onGotApiPayload={(data) => {
                    providers.value = data.providers || [];
                    reportTypes.value = data.reportTypes || [];
                }}
            >
                {{
                    filterSlot: (
                        <div className="grid grid-cols-12 gap-1 h-[46px]">
                            <SelectFilter
                                id="slc-provider"
                                label="Provedor..."
                                value={provider}
                                onChange={providerFilterChanged}
                                defaultVoidOption
                                defaultLabel="Todos os provedores"
                                options={providers.value.map((item) => ({ value: item, label: item }))}
                                className="col-span-2"
                            />
                            <SelectFilter
                                id="slc-report-type"
                                label="Tipo de relatório..."
                                value={rptType}
                                onChange={reportTypeFilterChanged}
                                defaultVoidOption
                                defaultLabel="Todos os tipos"
                                options={reportTypes.value.map((rt) => ({ value: rt.name, label: `(${rt.name}) - ${rt.description}` }))}
                                className="col-span-5"
                            />
                            <SelectFilter
                                id="slc-status"
                                label="Status..."
                                value={status}
                                onChange={statusFilterChanged}
                                defaultVoidOption
                                defaultLabel="Todos os status"
                                options={[
                                    { value: 'PENDING', label: 'PENDING' },
                                    { value: 'COMPLETED', label: 'COMPLETED' },
                                ]}
                                className="col-span-2"
                            />
                            <PageSearch
                                value={identification}
                                label="Buscar por identificação..."
                                onSearch={identificationFilterChanged}
                                filterCleanVisible={true}
                                filterCleanEnabled={Boolean(identification)}
                                onFilterClean={() => identificationFilterChanged('')}
                                configureVisible={false}
                                className="col-span-3"
                            />
                        </div>
                    ),
                    controlSlot: (
                        <div className="pt-4">
                            <Link
                                to="/data-provider/batch-report/form"
                                className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center dark:bg-blue-400"
                            >
                                Novo
                            </Link>
                        </div>
                    ),
                }}
            </PageList>
            <DocumentListModal
                id="batch-report-docs"
                show={showModal.value}
                onClose={() => {
                    showModal.value = false;
                    highlightedRowId.value = null;
                }}
                title={modalTitle.value}
                items={modalItems.value}
                itemType={modalType.value}
            />
        </Fragment>
    );
};

export default BatchReportPageList;
